import React, { useMemo } from 'react';

import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

import GlobalShortcutsPlatform, {
	type GlobalShortcutsItem,
	productLinksWorkspaceCloudIdFilter,
} from '@atlassian/global-shortcuts';
import { confluence } from '@atlassian/global-shortcuts/confluence';
import { loom } from '@atlassian/global-shortcuts/loom';
import { goals } from '@atlassian/global-shortcuts/goals';
import { opsgenie } from '@atlassian/global-shortcuts/opsgenie';
import { projects } from '@atlassian/global-shortcuts/projects';

import { assets } from '@atlassian/global-shortcuts/assets';
import {
	L1_MENU_ID,
	type MenuItemConfigData,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';

import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useLicenseStates } from '@atlassian/jira-tenant-context-controller/src/components/license-states/index.tsx';
import { InvitePeopleModalContextProvider } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/controllers/use-invite-people-modal/index.tsx';
import { useOrderedAppShortcuts } from './useOrderedAppShortcuts.tsx';
import { useTeamsAppShortcut } from './apps/teams/index.tsx';

type GlobalShortcutsProps = {
	appItemsData?: MenuItemConfigData[];
};

type GlobalShortcutsWithTeamsProps = GlobalShortcutsProps & {
	teamsAppConfig?: GlobalShortcutsItem;
};

const GlobalShortcutsComponent = ({
	appItemsData,
	teamsAppConfig,
}: GlobalShortcutsWithTeamsProps) => {
	const cloudId = useCloudId();
	const { data: orgId } = useOrgId();
	const licenceStates = useLicenseStates();

	// Filter product shortcuts based on licence states
	const licenceFilterMap = useMemo((): { [key: string]: GlobalShortcutsItem } => {
		// Map shortcut configs to menu identifiers
		const shortcutsMap = {
			[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluence(() => ''),
			[L1_MENU_ID.GLOBAL_APP_LOOM]: loom(() => ''),
			[L1_MENU_ID.GLOBAL_APP_GOALS]: goals(() => 'https://home.atlassian.com/goals'),
			[L1_MENU_ID.GLOBAL_APP_PROJECTS]: projects(() => 'https://home.atlassian.com/projects'),
			...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
			[L1_MENU_ID.GLOBAL_APP_ASSETS]: assets(() => '/jira/assets'),
			[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: opsgenie(() => ''),
		};

		if (licenceStates.core === 'ACTIVE') {
			if (licenceStates.serviceDesk === 'ACTIVE') {
				return {
					[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_CONFLUENCE],
					[L1_MENU_ID.GLOBAL_APP_LOOM]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_LOOM],
					[L1_MENU_ID.GLOBAL_APP_ASSETS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_ASSETS],
					[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_OPSGENIE],
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
				};
			}

			if (licenceStates.productDiscovery === 'ACTIVE') {
				return {
					[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_CONFLUENCE],
					[L1_MENU_ID.GLOBAL_APP_LOOM]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_LOOM],
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
				};
			}
		} else {
			if (licenceStates.serviceDesk === 'ACTIVE') {
				return {
					[L1_MENU_ID.GLOBAL_APP_ASSETS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_ASSETS],
					[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_OPSGENIE],
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
				};
			}

			if (licenceStates.productDiscovery === 'ACTIVE') {
				return {
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
				};
			}
		}

		return {
			[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_CONFLUENCE],
			[L1_MENU_ID.GLOBAL_APP_LOOM]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_LOOM],
			...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
			[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
			[L1_MENU_ID.GLOBAL_APP_PROJECTS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_PROJECTS],
		};
	}, [licenceStates, teamsAppConfig]);

	// sort filtered products based on order of appItemsData
	const orderedShortcuts = useOrderedAppShortcuts(licenceFilterMap, appItemsData);

	return (
		<GlobalShortcutsPlatform
			cloudId={cloudId}
			orgId={orgId}
			promoteSite
			shortcutItems={orderedShortcuts}
			product="jira"
			productLinksFilter={cloudId ? productLinksWorkspaceCloudIdFilter(cloudId) : undefined}
		/>
	);
};

// Wrapper required to use teams app shortcut hook
const GlobalShortcutsInvitePeopleWrapper = (props: GlobalShortcutsProps) => {
	const { component: teamsAppComponent, config: teamsAppConfig } = useTeamsAppShortcut();
	return (
		<>
			{teamsAppComponent}
			<GlobalShortcutsComponent {...props} teamsAppConfig={teamsAppConfig} />
		</>
	);
};

// The teams app will sit under its own feature gate / experiement
export const GlobalShortcutsWithInvitePeopleProvider = (props: GlobalShortcutsProps) => {
	return (
		<InvitePeopleModalContextProvider>
			<GlobalShortcutsInvitePeopleWrapper {...props} />
		</InvitePeopleModalContextProvider>
	);
};

// Teams is not included by default, so we export the component without it
export const GlobalShortcuts = (props: GlobalShortcutsProps) => {
	return <GlobalShortcutsComponent {...props} />;
};
